import React, { useCallback, useEffect, useRef, useState } from 'react';

export type MaintenanceStatus = {
  maintenance: boolean;
  tier?: 'small' | 'large' | string;
  until?: string | null;
  retry_after_seconds?: number;
  message?: string | null;
};

type Props = {
  statusUrl: string;
  productName: string;
  accentColor?: string;
  onClearSession: () => void;
  loginHref: string;
  children: React.ReactNode;
};

function formatIstClock(untilIso: string | null | undefined): string | null {
  if (!untilIso) return null;
  try {
    const d = new Date(untilIso);
    if (Number.isNaN(d.getTime())) return null;
    return new Intl.DateTimeFormat('en-IN', {
      timeZone: 'Asia/Kolkata',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    }).format(d);
  } catch {
    return null;
  }
}

function formatCountdown(totalSeconds: number): string {
  const s = Math.max(0, Math.floor(totalSeconds));
  const m = Math.floor(s / 60);
  const r = s % 60;
  if (m <= 0) return `${r}s`;
  return `${m}m ${String(r).padStart(2, '0')}s`;
}

/**
 * Full-page deploy maintenance gate.
 * Fail-open: unreachable status never forces maintenance.
 * On true→false (or countdown done + clear): wipe session once, then send to login.
 */
export function MaintenanceGate({
  statusUrl,
  productName,
  accentColor = '#c45c26',
  onClearSession,
  loginHref,
  children,
}: Props) {
  const [status, setStatus] = useState<MaintenanceStatus | null>(null);
  const [nowMs, setNowMs] = useState(() => Date.now());
  const wasMaintenance = useRef(false);
  const reauthDoneKey = useRef<string | null>(null);

  const fetchStatus = useCallback(async () => {
    try {
      const res = await fetch(statusUrl, {
        method: 'GET',
        headers: { Accept: 'application/json' },
        cache: 'no-store',
      });
      if (!res.ok) return; // fail-open
      const data = (await res.json()) as MaintenanceStatus;
      setStatus(data);
    } catch {
      // fail-open — do not set maintenance on network errors
    }
  }, [statusUrl]);

  useEffect(() => {
    void fetchStatus();
  }, [fetchStatus]);

  const active = Boolean(status?.maintenance);
  const pollMs = active && (status?.retry_after_seconds ?? 900) <= 90 ? 5000 : 20000;

  useEffect(() => {
    const id = window.setInterval(() => {
      void fetchStatus();
    }, pollMs);
    const onVis = () => {
      if (document.visibilityState === 'visible') void fetchStatus();
    };
    const onFocus = () => {
      void fetchStatus();
    };
    document.addEventListener('visibilitychange', onVis);
    window.addEventListener('focus', onFocus);
    return () => {
      window.clearInterval(id);
      document.removeEventListener('visibilitychange', onVis);
      window.removeEventListener('focus', onFocus);
    };
  }, [fetchStatus, pollMs]);

  useEffect(() => {
    if (!active) return;
    const id = window.setInterval(() => setNowMs(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [active]);

  useEffect(() => {
    if (active) {
      wasMaintenance.current = true;
      return;
    }
    if (!wasMaintenance.current) return;
    wasMaintenance.current = false;
    const epoch = status?.until || String(Date.now());
    const guardKey = `maintenance_reauth_done_${epoch}`;
    if (reauthDoneKey.current === guardKey) return;
    try {
      if (sessionStorage.getItem(guardKey) === '1') return;
      sessionStorage.setItem(guardKey, '1');
    } catch {
      /* ignore */
    }
    reauthDoneKey.current = guardKey;
    onClearSession();
    window.location.replace(loginHref);
  }, [active, status?.until, onClearSession, loginHref]);

  // Countdown hit zero: re-poll; if still clear, reauth effect handles it
  useEffect(() => {
    if (!active || !status?.until) return;
    const untilMs = new Date(status.until).getTime();
    if (Number.isNaN(untilMs)) return;
    if (nowMs < untilMs) return;
    void fetchStatus();
  }, [active, status?.until, nowMs, fetchStatus]);

  if (!active) {
    return <>{children}</>;
  }

  const untilIso = status?.until ?? null;
  const untilMs = untilIso ? new Date(untilIso).getTime() : NaN;
  const remaining = Number.isFinite(untilMs)
    ? Math.max(0, Math.round((untilMs - nowMs) / 1000))
    : status?.retry_after_seconds ?? 0;
  const clock = formatIstClock(untilIso);
  const message =
    status?.message ||
    (status?.tier === 'small'
      ? `We're applying a quick update to ${productName}. Please try again in about a minute.`
      : `We're updating ${productName}. Please try again in about 15 minutes.`);

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '24px',
        background: '#f7f6f2',
        color: '#1c1917',
        fontFamily:
          'ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif',
      }}
    >
      <div
        style={{
          width: '100%',
          maxWidth: 440,
          background: '#fff',
          border: '1px solid #e7e5e4',
          borderRadius: 12,
          overflow: 'hidden',
          boxShadow: '0 10px 30px rgba(28, 25, 23, 0.06)',
        }}
      >
        <div style={{ height: 4, background: accentColor }} />
        <div style={{ padding: '28px 28px 24px' }}>
          <p
            style={{
              margin: 0,
              fontSize: 12,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              color: '#78716c',
              fontWeight: 600,
            }}
          >
            {productName}
          </p>
          <h1 style={{ margin: '10px 0 0', fontSize: 22, fontWeight: 650, lineHeight: 1.25 }}>
            Temporarily unavailable
          </h1>
          <p style={{ margin: '12px 0 0', fontSize: 15, lineHeight: 1.5, color: '#57534e' }}>
            {message}
          </p>
          {clock ? (
            <p style={{ margin: '16px 0 0', fontSize: 14, color: '#44403c' }}>
              Back around <strong>{clock} IST</strong>
            </p>
          ) : null}
          <p
            style={{
              margin: '8px 0 0',
              fontSize: 28,
              fontVariantNumeric: 'tabular-nums',
              fontWeight: 650,
              color: '#1c1917',
            }}
            aria-live="polite"
          >
            {formatCountdown(remaining)}
          </p>
          <button
            type="button"
            onClick={() => void fetchStatus()}
            style={{
              marginTop: 22,
              width: '100%',
              border: 'none',
              borderRadius: 8,
              padding: '12px 16px',
              background: accentColor,
              color: '#fff',
              fontSize: 15,
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            Try again
          </button>
        </div>
      </div>
    </div>
  );
}
